export type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type BlockType = "work" | "work_telework" | "free" | "sleep" | "sport" | "meal" | "transport" | "rest" | "blocked" | "nutrition";

export type FlexType = "fixed" | "flexible";

export type PersistenceType = "permanent" | "exceptional";

export interface PlanningBlock {
  id: string;
  day: DayKey;
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
  type: BlockType;
  flex: FlexType;
  persistence?: PersistenceType; // default "permanent"
  location?: string | null;
  note?: string | null;
}

export interface PlanningRules {
  telework_allows_gym: boolean;
  min_rest_hours_between_sessions: number;
  preferred_training_windows: string[];
  sleep_target: { bed: string; wake: string };
}

export interface PlanningWeek {
  timezone: string;
  week_template: Omit<PlanningBlock, "id">[];
  rules: PlanningRules;
}

export const DAYS: { key: DayKey; label: string; short: string }[] = [
  { key: "mon", label: "Lundi", short: "Lun" },
  { key: "tue", label: "Mardi", short: "Mar" },
  { key: "wed", label: "Mercredi", short: "Mer" },
  { key: "thu", label: "Jeudi", short: "Jeu" },
  { key: "fri", label: "Vendredi", short: "Ven" },
  { key: "sat", label: "Samedi", short: "Sam" },
  { key: "sun", label: "Dimanche", short: "Dim" },
];

export const BLOCK_TYPES: { type: BlockType; label: string; color: string; icon: string }[] = [
  { type: "work", label: "Travail", color: "bg-muted text-muted-foreground border-muted-foreground/20", icon: "💼" },
  { type: "work_telework", label: "Télétravail", color: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30", icon: "🏠" },
  { type: "free", label: "Disponible", color: "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30", icon: "✅" },
  { type: "sleep", label: "Sommeil", color: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30", icon: "🌙" },
  { type: "sport", label: "Sport", color: "bg-primary/15 text-primary border-primary/30", icon: "🏋️" },
  { type: "meal", label: "Repas", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30", icon: "🍽️" },
  { type: "transport", label: "Transport", color: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30", icon: "🚗" },
  { type: "rest", label: "Repos", color: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30", icon: "🛋️" },
  { type: "blocked", label: "Indisponible", color: "bg-neutral-500/15 text-neutral-700 dark:text-neutral-300 border-neutral-500/30", icon: "🚫" },
  { type: "nutrition", label: "Nutrition", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", icon: "🥗" },
];

export const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 6..23

export const DEFAULT_RULES: PlanningRules = {
  telework_allows_gym: true,
  min_rest_hours_between_sessions: 12,
  preferred_training_windows: ["06:30-09:00", "18:00-21:30"],
  sleep_target: { bed: "23:00", wake: "07:00" },
};

export function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

export function minutesToTime(m: number): string {
  const h = Math.floor(m / 60);
  const mins = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function blocksOverlap(a: PlanningBlock, b: PlanningBlock): boolean {
  if (a.day !== b.day || a.id === b.id) return false;
  const aStart = timeToMinutes(a.start);
  const aEnd = timeToMinutes(a.end);
  const bStart = timeToMinutes(b.start);
  const bEnd = timeToMinutes(b.end);
  return aStart < bEnd && bStart < aEnd;
}

/**
 * Generate a deterministic block ID from stable block properties.
 * Uses day + start + end + type + flex + persistence + a per-duplicate index
 * so two blocks with identical properties on the same day get distinct IDs.
 */
export function deterministicBlockId(
  block: Omit<PlanningBlock, "id">,
  dupIndex: number
): string {
  const key = `${block.day}|${block.start}|${block.end}|${block.type}|${block.flex}|${block.persistence ?? "permanent"}|${dupIndex}`;
  // Simple deterministic hash → hex string
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash + key.charCodeAt(i)) | 0;
  }
  const hex = (hash >>> 0).toString(16).padStart(8, "0");
  return `blk-${block.day}-${hex}`;
}

/**
 * Hydrate an array of blocks without IDs into PlanningBlocks with stable deterministic IDs.
 * Blocks with identical (day, start, end, type, flex, persistence) get incrementing dupIndex.
 */
export function hydrateBlocksStable(blocks: Omit<PlanningBlock, "id">[]): PlanningBlock[] {
  const counters = new Map<string, number>();
  return blocks.map((b) => {
    const fingerprint = `${b.day}|${b.start}|${b.end}|${b.type}|${b.flex}|${b.persistence ?? "permanent"}`;
    const idx = counters.get(fingerprint) ?? 0;
    counters.set(fingerprint, idx + 1);
    return { ...b, id: deterministicBlockId(b, idx) };
  });
}
